import Hue from '@assets/hue_yellow.svg';
import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import PriceDisplayText from '@atoms/PriceDisplayText/PriceDisplayText';
import { easeInOut } from '@config/animations.config';
import { IProductCard } from '@models/IProductCard';
import Tags from '@molecules/Tags/Tags';
import updateDataLayer from '@utils/helpers/gtm';
import mapToNewTrackingData from '@utils/helpers/mapToNewTrackingData';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useState } from 'react';

interface Props extends IProductCard {
  readonly isCompact?: boolean;
  readonly isLargeCard?: boolean;
  readonly dataTestId?: string;
}

const GridProductCard = ({
  buyNowLink,
  fromPriceLabel,
  hidePriceFromSavePill,
  image,
  isCompact,
  isLargeCard,
  learnMoreLink,
  name,
  newProductLabel,
  pricingData,
  savePriceLabel,
  shippingLabel,
  statusLabels,
  thumbnail,
  trackingData,
  vatLabel,
  dataTestId,
}: Props) => {
  const [isHovered, setHover] = useState(false);
  const isMobile = useMediaQuery(1440);
  const [ctasContainerRef, { clientHeight: ctasContainerHeight }] =
    useResizeObserver();
  const hasStackedCTAs = ctasContainerHeight > 50; // random number chosen to easily detect whether the CTAs are stacked
  const trackingDataJson = JSON.stringify(trackingData ?? '');

  const handleIntentToBuyEvent = (contentType: string) => {
    if (!trackingData) return;

    updateDataLayer({
      event: 'intentToBuy',
      content_type: contentType,
      items: [mapToNewTrackingData(trackingData)],
    });
  };

  const handleSelectItemEvent = () => {
    if (!trackingData) return;

    updateDataLayer({
      event: 'selectItem',
      ecommerce: {
        currency: trackingData.currency,
        items: [mapToNewTrackingData(trackingData)],
      },
    });
  };

  return (
    <div
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      track-product-impression="true"
      data-product={trackingDataJson}
      className={classNames(
        'pointer-events-auto relative flex h-full w-full flex-col justify-end gap-5 overflow-hidden rounded-[10px] bg-grey-200 dark:bg-grey-600',
        isLargeCard ? 'px-5 py-5 lg:py-8 xl:py-14' : 'px-5 pt-5 pb-5 lg:pb-7.5'
      )}
      data-testid={dataTestId}
    >
      {image && (
        <Image
          image={image}
          classes="h-2/5 md:h-1/2 z-5 absolute top-0 right-0 left-0 m-2.5 md:m-5"
          imageClasses="rounded-[10px] object-cover h-full"
        />
      )}

      {(savePriceLabel || newProductLabel || statusLabels) && !isCompact && (
        <div className="absolute top-0 left-0 z-20 flex w-full flex-row flex-wrap gap-4 p-5">
          <Tags
            dataTestId="Product-Card-Tag"
            hidePriceFromSavePill={hidePriceFromSavePill}
            newProductLabel={newProductLabel}
            pricingData={pricingData}
            savePriceLabel={savePriceLabel}
            statusLabels={statusLabels}
          />
        </div>
      )}

      <div
        className={classNames(
          'relative flex h-full w-full flex-col',
          {
            'gap-y-5 pt-10 md:pt-[150px] lg:gap-y-7.5 lg:pt-[250px] xl:gap-y-5 xl:pt-[120px] 2xl:pt-[200px]':
              isLargeCard && image,
          },
          {
            'gap-y-5 lg:gap-y-13 xl:gap-y-[90px]': isLargeCard && !image,
          },
          {
            'gap-y-2.5 pt-2.5 md:gap-y-5': !isLargeCard,
          }
        )}
      >
        <motion.div
          initial="initial"
          className={classNames(
            'relative flex w-full shrink-0 items-center justify-center',
            isLargeCard
              ? 'h-[200px] md:h-[270px] lg:h-[340px]'
              : 'h-[140px] md:h-[190px] lg:h-[210px] xl:h-[180px]'
          )}
          animate={isHovered && !isMobile ? 'hover' : 'initial'}
          variants={{
            hover: hasStackedCTAs
              ? { y: -20, scale: 0.85 }
              : { y: -10, scale: 1.05 },
            initial: { y: 0, scale: 1 },
          }}
          transition={easeInOut}
        >
          {thumbnail && (
            <a
              href={learnMoreLink?.url || buyNowLink?.url}
              onClick={() => {
                if (learnMoreLink?.url)
                  handleIntentToBuyEvent('product picture');
                else if (buyNowLink?.url) handleSelectItemEvent();
              }}
              aria-label={name}
              className="flex h-full w-full"
            >
              <Image
                image={thumbnail}
                classes="z-10 w-full h-full"
                imageClasses="w-full h-full object-contain"
                sizes="(max-width: 768px) 376px, 500px"
                dataTestId="Product-Card-Image"
              />

              <motion.div
                initial="initial"
                animate={isHovered && !isMobile ? 'hover' : 'initial'}
                variants={{
                  hover: { opacity: 0.2 },
                  initial: { opacity: 0 },
                }}
                className="pointer-events-none absolute top-4 left-0 z-0 flex h-full scale-[1.1] items-center justify-center"
              >
                <img
                  src={`${Hue}`}
                  alt="hue"
                  className="hidden w-full object-contain dark:block"
                />
              </motion.div>
            </a>
          )}
        </motion.div>

        <motion.div
          className="flex grow flex-col items-center justify-center"
          initial="initial"
          animate={isHovered && !isMobile ? 'hover' : 'initial'}
          variants={{
            hover: {
              y: isLargeCard ? -30 : -(ctasContainerHeight - 20),
            },
            initial: { y: 0 },
          }}
          transition={easeInOut}
        >
          <div
            style={{
              paddingBottom: isLargeCard && !isMobile ? ctasContainerHeight : 0,
            }}
            className={classNames(
              'flex w-full grow flex-col items-center justify-center dark:text-white',
              isLargeCard ? 'lg:w-3/4 xl:w-4/5 2xl:w-3/4' : 'md:w-4/5 xl:w-full'
            )}
          >
            {name && (
              <p
                className={classNames(
                  'text-center',
                  isLargeCard
                    ? `${classNames('mb-5 lg:mb-7.5', isMobile ? 'h5' : 'h3')}`
                    : 'h6 mb-2.5'
                )}
                data-testid="Product-Card-Name"
              >
                {name}
              </p>
            )}

            {pricingData?.formattedCalculatedPrice && (
              <PriceDisplayText
                pricingData={pricingData}
                vatLabel={vatLabel}
                shippingLabel={shippingLabel}
                fromPriceLabel={fromPriceLabel}
                centerText
                containerClassName={classNames({ 'mt-auto': !isLargeCard })}
                containerFontSize={classNames({
                  'body-one': isLargeCard,
                  'body-three': !isLargeCard && !isMobile,
                  'body-four': !isLargeCard && isMobile,
                })}
                taxesAnimationProps={{
                  initial: 'initial',
                  animate:
                    isHovered && !isMobile && !isLargeCard
                      ? 'hover'
                      : 'initial',
                  variants: {
                    hover: { opacity: 0 },
                    initial: { opacity: 1 },
                  },
                  transition: {
                    duration: 0.3,
                  },
                }}
                taxesTopMarginClassName={classNames(
                  'mt-[5px]',
                  isLargeCard ? 'md:mt-2.5' : 'xl:mt-2.5'
                )}
                taxesFontSize={classNames({
                  'body-one': isLargeCard,
                  'body-three': !isLargeCard && !isMobile,
                  'body-four': !isLargeCard && isMobile,
                })}
                dataTestId="Product-Card-Price-Display-Text"
              />
            )}
          </div>
        </motion.div>

        <motion.div
          initial="initial"
          animate={isHovered && !isMobile ? 'hover' : 'initial'}
          variants={{
            hover: { opacity: 1 },
            initial: { opacity: isMobile ? 1 : 0 },
          }}
          transition={{
            duration: 0.3,
          }}
          ref={ctasContainerRef}
          className="flex w-full flex-wrap items-center justify-center gap-2.5 md:gap-5 xl:absolute xl:bottom-0 xl:gap-2.5"
          data-testid="Product-Card-Item-Hover"
        >
          {learnMoreLink?.name && learnMoreLink?.url && (
            <Button
              className={classNames('text-center', {
                'max-md:w-full': !isLargeCard,
              })}
              link={learnMoreLink}
              secondary
              linkCallback={() =>
                handleIntentToBuyEvent(learnMoreLink.name || 'Learn more')
              }
              dataTestId="Product-Card-Learn More"
            />
          )}

          {buyNowLink?.url && buyNowLink?.name && (
            <a
              href={buyNowLink.url}
              track-product-click="true"
              data-product={trackingDataJson}
              className={classNames('cta primary text-center', {
                'max-md:w-full': !isLargeCard,
              })}
              data-testid="Product-Card-Buy-Now"
              onClick={handleSelectItemEvent}
            >
              {buyNowLink.name}
            </a>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default GridProductCard;
